import { useState } from 'react';
import { ACTIVE_COMPANIES_ONLY_QUERY, Size, counterpartyQuery, suggestionRowsFor, t, type SuggestionRow, type SuggestionSource, newDocumentTitle } from '@bms2/shared';
import { AlertBanner, DatePicker, Dropdown, NumericInput, TextInput } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import { ModalField } from '../../components/modals/ModalShell';
import { PAGES_PATH, PICKER_CAP, VENDOR_CREDITS_PATH, apiFetch, post } from './api';
import { useAddNew } from './AddNewHost';
import { useDocumentToday } from '../../shell/ViewerZone';

/**
 * Raising a Vendor Credit from its own list (decision 0449). 🔨 It is a modal
 * and not a page: a credit carries no table of lines, so decision 0409 puts it
 * beside the revaluation's modal rather than beside the Bill's page.
 *
 * 🔨 It is unmounted rather than hidden when it closes, so reopening it does
 * not show the last credit's draft: the modal holds the vendor, the date, the
 * amount and the reason in its own state.
 */
export interface VendorCreditModalProps {
  open: boolean;
  apiBaseUrl?: string;
  onCancel: () => void;
  onCreated: (id: string) => void;
  /** The vendor the list was already filtered to, where one was; the field opens on it. */
  vendor?: { id: string; label: string } | null;
  /** T346 (decision 1515): what a message in the accounting mailbox read, filled in for review; absent opens the window empty. */
  initial?: { creditDate?: string | null; amountCents?: number | null; reason?: string | null } | null;
}

export function VendorCreditModal({ open, apiBaseUrl = '', onCancel, onCreated, vendor = null, initial = null }: VendorCreditModalProps) {
  const addNew = useAddNew(apiBaseUrl);
  const [chosen, setChosen] = useState<{ id: string; label: string } | null>(vendor);
  // 🔒 Opens on today in the viewer's own zone, through the hook every document form reads (decision 1030).
  const today = useDocumentToday();
  const [creditDate, setCreditDate] = useState(initial?.creditDate || today);
  const [amountCents, setAmountCents] = useState<number | null>(initial?.amountCents ?? null);
  const [reason, setReason] = useState(initial?.reason ?? '');
  const [bill, setBill] = useState<{ id: string; label: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const searchIn = (recordType: 'CompanyRecord' | 'Bill') => async (term: string, signal: AbortSignal): Promise<SuggestionRow[]> => {
    // 🔒 The vendor field asks the company list for vendors alone (T184; decision 0637), so it never offers a company the save refuses (found in review, 2026-09-22).
    const side = recordType === 'CompanyRecord' ? `${counterpartyQuery('VendorCredit')}&${ACTIVE_COMPANIES_ONLY_QUERY}&` : '';
    const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/${recordType}?${side}${PICKER_CAP}&q=${encodeURIComponent(term)}`, { signal });
    if (!response.ok) return [];
    const body = (await response.json()) as { rows: SuggestionSource[] };
    return suggestionRowsFor(recordType, body.rows);
  };

  const submit = async (): Promise<void> => {
    if (!chosen) return setError(t('vendorCredit.new.needsVendor'));
    if (!creditDate) return setError(t('vendorCredit.new.needsDate'));
    if (!amountCents || amountCents <= 0) return setError(t('vendorCredit.new.needsAmount'));
    if (!reason.trim()) return setError(t('vendorCredit.new.needsReason'));
    setBusy(true);
    const saved = await post<{ id: string }>(
      VENDOR_CREDITS_PATH,
      {
        vendorCompanyId: chosen.id, creditDate, amountCents, reason: reason.trim(),
        // 🔨 A credit settles only its own vendor's bills, and the server names that refusal rather than this screen guessing at it.
        ...(bill ? { sourceBillId: bill.id } : {}),
      },
      apiBaseUrl,
    );
    setBusy(false);
    if (!saved.ok) return setError(saved.message);
    onCreated(saved.data.id);
  };

  return (
    <Modal
      open={open}
      title={newDocumentTitle('VendorCredit')}
      subtitle={t('vendorCredit.new.hint')}
      onCancel={onCancel}
      dataModal="newVendorCredit"
      actions={[
        { kind: 'primary', label: busy ? t('vendorCredit.new.saving') : t('vendorCredit.new.save'), onAct: () => submit() },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} /> : null}
      <ModalField label={t('vendorCredit.new.vendor')}>
        <span data-vendor-credit-field="vendor">
          <Dropdown size={Size.Medium} label={t('vendorCredit.new.vendor')} labelHidden entityLabel={t('vendorCredit.new.vendorEntity')} search={searchIn('CompanyRecord')} value={chosen?.label ?? null} onSelect={(row) => setChosen({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord', role: 'vendor' }, (row) => setChosen({ id: row.id, label: row.line1 }))} required />
        </span>
      </ModalField>
      <DatePicker size={Size.Small} label={t('vendorCredit.new.creditDate')} value={creditDate} onChange={setCreditDate} mode="date" name="vendorCreditDate" />
      <NumericInput size={Size.Medium} label={t('vendorCredit.new.amount')} kind="currency" value={amountCents} onChange={setAmountCents} required name="vendorCreditAmount" />
      <TextInput size={Size.Large} label={t('vendorCredit.new.reason')} value={reason} onChange={setReason} required name="vendorCreditReason" />
      {/* The bill the credit came off, where there is one; a credit granted against no bill at all is ordinary and leaves it empty. */}
      <ModalField label={t('vendorCredit.new.sourceBill')}>
        <span data-vendor-credit-field="sourceBill">
          <Dropdown size={Size.Medium} label={t('vendorCredit.new.sourceBill')} labelHidden entityLabel={t('vendorCredit.new.billEntity')} search={searchIn('Bill')} value={bill?.label ?? null} onSelect={(row) => setBill({ id: row.id, label: row.line1 })} onAddNew={() => addNew.open({ kind: 'document', documentType: 'Bill' }, (row) => setBill({ id: row.id, label: row.line1 }))} />
        </span>
      </ModalField>
      {addNew.host}
    </Modal>
  );
}
